import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { logOutcome, recordMove } from "@omina/core";
import type { Goal, GoalType, LoopData, MetAt, OutcomeResult } from "@omina/core";
import { api, apiError } from "./api";
import type { LegacyExport } from "./legacy-import";

/**
 * Server state, one hook per endpoint.
 *
 * Query keys mirror route paths — `["api", "loop", "summary"]` is
 * `GET /api/loop/summary`. That is not decoration: invalidating a prefix then
 * means "everything under this path is stale", which is exactly how the loop
 * behaves. Logging an outcome invalidates `["api"]` down to contacts, outcomes
 * and the summary in one call, because all three did change.
 */

// ─────────────────────────────────────────────────────────────
// Who am I
// ─────────────────────────────────────────────────────────────

/**
 * The session, the org and whether intake is done — one request.
 *
 * A 401 is not an error here, it is the answer "signed out": it resolves to
 * null so the router's guard can branch on it instead of catching. Any other
 * failure is a real failure and throws.
 */
export const meQuery = queryOptions({
  queryKey: ["api", "me"],
  queryFn: async () => {
    const res = await api.me.$get();
    if (res.status === 401) return null;
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
  // Signed-out is a stable answer; retrying it just delays the redirect.
  retry: false,
  staleTime: 30_000,
});

export type Me = Awaited<ReturnType<NonNullable<typeof meQuery.queryFn>>>;

export function useMe() {
  return useQuery(meQuery);
}

export const profileQuery = queryOptions({
  queryKey: ["api", "profile"],
  queryFn: async () => {
    const res = await api.profile.$get();
    // No profile yet is a state, not an error — she has not done intake.
    if (res.status === 404) return null;
    if (!res.ok) throw await apiError(res);
    return (await res.json()).profile;
  },
});

export function useProfile() {
  return useQuery(profileQuery);
}

/**
 * Request bodies are declared here, not inferred.
 *
 * The API validates with `parseBody(await jsonOf(...), schema)` inside each
 * handler rather than with a `zValidator` middleware, so hono's RPC client sees
 * no input type for them — `InferRequestType` resolves to `{}` and every body
 * would be silently unchecked. Mirroring the server's zod schema by hand is
 * what keeps these call sites honest until the API adopts the validator.
 * Response types are still fully inferred; only inputs are written twice.
 */
/**
 * Attach a JSON body to a call hono types as param-only.
 *
 * Same root cause as the note above, one step worse: on a `/:id` route the
 * inferred input is exactly `{ param }`, so excess-property checking rejects
 * `json` at compile time even though the client sends it correctly at
 * runtime. The body stays checked — against `B`, the hand-mirrored interface
 * at the call site — and only the handoff to hono is widened.
 */
function withBody<A extends object, B>(args: A, json: B): A {
  return { ...args, json } as A;
}

export interface ProfileBody {
  name: string;
  who?: string;
  product?: string;
  targetIndustry?: string | null;
  /** The coarse axis, on top of targetIndustry — never instead of it. */
  audience?: "b2b" | "b2c" | null;
  buyerRole?: string | null;
  buyerCompany?: string | null;
  buyerPain?: string | null;
  industryOutsider?: boolean | null;
  sector?: string | null;
  stage?: string | null;
  location?: string | null;
  birthDate?: string | null;
  birthTime?: string | null;
  birthPlace?: string | null;
  /** Full name at birth — numerology only, distinct from `name`. */
  fullName?: string | null;
  patterns?: Record<string, string>;
}

/**
 * Write the profile.
 *
 * `patterns` merges server-side rather than replacing, so the interview can
 * confirm one layer without erasing the five the chart proposed.
 */
export function useSaveProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProfileBody) => {
      const res = await api.profile.$put({ json: body });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).profile;
    },
    onSuccess: (profile) => {
      qc.setQueryData(profileQuery.queryKey, profile);
      // `me` carries profileExists, and this call is usually what flips it.
      void qc.invalidateQueries({ queryKey: ["api", "me"] });

      // Everything downstream of the industry and the location, which is most
      // of the product: the radar filters events by her vertical and searches
      // on her city, and the map is composed from both. Without this she
      // switches from clinics to SaaS, walks back to the radar, and is served
      // the cached clinics answer — the change looks like it did nothing.
      void qc.invalidateQueries({ queryKey: ["api", "radar"] });
      void qc.invalidateQueries({ queryKey: ["api", "maps"] });
      void qc.invalidateQueries({ queryKey: ["api", "event-sources"] });
    },
  });
}

/**
 * Her city, changed from wherever she noticed it was wrong.
 *
 * Separate from `useSaveProfile` because the endpoint is separate, and the
 * endpoint is separate because the profile PUT writes every column — see the
 * note on `PUT /api/profile/location`. The feed holds a city and nothing else.
 *
 * The invalidations are the same set the full save uses, minus the ones only
 * an industry change touches. The location is what the radar matches calendars
 * on and what the web scan searches near, so leaving the cached answer in place
 * would show her the old city's rooms after she told Omina she had moved — the
 * change looking like it did nothing, which is exactly the failure the note in
 * `useSaveProfile` records.
 */
export function useSaveLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (location: string | null) => {
      const res = await api.profile.location.$put({ json: { location } });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).profile;
    },
    onSuccess: (profile) => {
      qc.setQueryData(profileQuery.queryKey, profile);
      void qc.invalidateQueries({ queryKey: ["api", "radar"] });
      void qc.invalidateQueries({ queryKey: ["api", "events"] });
      void qc.invalidateQueries({ queryKey: ["api", "event-sources"] });
    },
  });
}

/**
 * Compute the chart.
 *
 * A mutation, not a query, even though it reads like one: it geocodes, writes
 * the birth data and the computed personality onto the profile, and intake
 * fires it from a debounce rather than from a render.
 */
export function useChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { birthDate: string; birthTime?: string; birthPlace?: string }) => {
      const res = await api.chart.$post({ json: body });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "profile"] });
      void qc.invalidateQueries({ queryKey: ["api", "me"] });
    },
  });
}

export type Chart = Awaited<ReturnType<ReturnType<typeof useChart>["mutateAsync"]>>;

export const settingsQuery = queryOptions({
  queryKey: ["api", "settings"],
  queryFn: async () => {
    const res = await api.settings.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useSettings() {
  return useQuery(settingsQuery);
}

/**
 * What this workspace has spent on third-party APIs this month.
 *
 * Read-only and org-scoped. `outOfCredits` is the field that matters: it names
 * a provider that stopped answering because an account is empty, which is
 * otherwise indistinguishable from the product being broken.
 */
export const usageQuery = queryOptions({
  queryKey: ["api", "usage"],
  queryFn: async () => {
    const res = await api.usage.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useUsage() {
  return useQuery(usageQuery);
}

/** The active track. Everything downstream — the map, the draft, the prep
 *  card — reads it, so it invalidates broadly. */
export function useSetActiveGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (activeGoal: GoalType) => {
      const res = await api.settings.$put({ json: { activeGoal } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Goals and the questions each one asks
// ─────────────────────────────────────────────────────────────

export const goalsQuery = queryOptions({
  queryKey: ["api", "goals"],
  queryFn: async () => {
    const res = await api.goals.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useGoals() {
  return useQuery(goalsQuery);
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { type: GoalType; priority?: number; notes?: string }) => {
      const res = await api.goals.$post({ json: body });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api", "goals"] }),
  });
}

export interface GoalPatchBody {
  status?: "active" | "paused" | "completed";
  priority?: number;
  notes?: string | null;
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & GoalPatchBody) => {
      const res = await api.goals[":id"].$patch(
        withBody({ param: { id } }, body satisfies GoalPatchBody),
      );
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api", "goals"] }),
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.goals[":id"].$delete({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api", "goals"] }),
  });
}

/** The answers this goal asks for. Keyed by goal id, because the shape of the
 *  form depends on which track the goal is. */
export function goalProfileQuery(id: string) {
  return queryOptions({
    queryKey: ["api", "goals", id, "profile"],
    queryFn: async () => {
      const res = await api.goals[":id"].profile.$get({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export function useGoalProfile(id?: string) {
  return useQuery({ ...goalProfileQuery(id ?? ""), enabled: Boolean(id) });
}

/**
 * Saving a goal profile invalidates broadly rather than just its own key: the
 * customer profile feeds the buyer wording that the radar, the drafts and the
 * prep card are all built from, so a stale map after editing the ICP is the
 * exact confusion this avoids.
 */
export function useSaveGoalProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, profile }: { id: string; profile: Record<string, unknown> }) => {
      const res = await api.goals[":id"].profile.$put(
        withBody({ param: { id } }, profile),
      );
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api"] }),
  });
}

/** The company, as opposed to the founder. */
export const orgProfileQuery = queryOptions({
  queryKey: ["api", "org-profile"],
  queryFn: async () => {
    const res = await api["org-profile"].$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useOrgProfile() {
  return useQuery(orgProfileQuery);
}

export function useSaveOrgProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profile: {
      productName?: string | null;
      productDescription?: string | null;
      website?: string | null;
      stage?: string | null;
      industries?: string[] | null;
      location?: string | null;
      teamSize?: number | null;
      foundedYear?: number | null;
      annualRevenue?: string | null;
    }) => {
      const res = await api["org-profile"].$put({ json: profile });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    // Same reason as above: `product`, `stage` and the industry all feed the
    // map and the radar through `founderProfile`.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api"] }),
  });
}

// ─────────────────────────────────────────────────────────────
// Where is my ICP
// ─────────────────────────────────────────────────────────────

export const mapsQuery = queryOptions({
  queryKey: ["api", "maps"],
  queryFn: async () => {
    const res = await api.maps.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).maps;
  },
});

export function useMaps() {
  return useQuery(mapsQuery);
}

export function mapQuery(goal: Goal) {
  return queryOptions({
    queryKey: ["api", "maps", goal],
    queryFn: async () => {
      const res = await api.maps[":goal"].$get({ param: { goal } });
      // An org that has never generated this track has no map. Honest empty
      // beats a spinner that never resolves.
      if (res.status === 404) return null;
      if (!res.ok) throw await apiError(res);
      // The whole envelope, not just `map`: `stale` says whether this still
      // describes the industry she sells into, and the page has to be able to
      // say so rather than render another industry's rooms silently.
      return res.json();
    },
  });
}

export function useMap(goal: Goal) {
  return useQuery(mapQuery(goal));
}

export const roomsQuery = queryOptions({
  queryKey: ["api", "rooms"],
  queryFn: async () => {
    const res = await api.rooms.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).rooms;
  },
});

export function useRooms() {
  return useQuery(roomsQuery);
}

/**
 * Compose one track of the map.
 *
 * The `mode` in the answer is not diagnostics — it is what the page tells her:
 * "ai" composed, "draft" un-personalised, "empty" means the curated base does
 * not cover her industry yet. All four are real product states (rule #2).
 */
export function useGenerateMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (goal: GoalType) => {
      const res = await api.maps.generate.$post({ json: { goal } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "maps"] });
    },
  });
}

/** Scan the public web. Candidates are held in the page, never cached: they
 *  are unverified until she says otherwise, and a cache would outlive that. */
export function useRadarScan() {
  return useMutation({
    mutationFn: async (goal: GoalType) => {
      const res = await api.radar.scan.$post({ json: { goal } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

/** See ProfileBody: inputs are mirrored by hand, responses are inferred. */
export interface RoomBody {
  name: string;
  kind: string;
  url: string;
  why?: string;
  angle?: string;
  date?: string | null;
  location?: string | null;
  vertical?: string | null;
  fit: { cust: number; auth: number; fund: number };
  /** A path on this origin — the artwork proxy, never a third party's url.
   *  Dropped on approval before this existed, which is why a show with cover
   *  art in the queue arrived on the map wearing a monogram. */
  imageUrl?: string | null;
  /** The resolved show, as a hint that this room is a podcast. The server
   *  re-reads the facts rather than trusting these; see the route. */
  show?: { id: string } | null;
}

/** The moment rule #1 is satisfied: a human looked at it. */
export function useApproveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (room: RoomBody) => {
      const res = await api.rooms.$post({ json: room });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).room;
    },
    onSuccess: () => {
      // The map merges approved rooms, so both go stale together.
      void qc.invalidateQueries({ queryKey: ["api", "rooms"] });
      void qc.invalidateQueries({ queryKey: ["api", "maps"] });
    },
  });
}

/**
 * What is on in her city, from calendars a human already vouched for.
 *
 * The complement to the scan: that one asks the web a question and starts
 * over each time, this one reads a base the cron keeps filled. Both end at
 * the same approve button, because rule #1 is about who decides.
 */
/**
 * The browser's IANA zone, for the day filter.
 *
 * Sent only when she has picked a day, because that is the only question whose
 * answer depends on it — the whole-window feed is a range and lands on the
 * same rooms in any zone. Her local day, though, is not a UTC day: a San
 * Francisco room at 5pm on the 25th is stored as midnight on the 26th, so
 * without this the strip answers Wednesday with Tuesday's evening.
 *
 * A name rather than an offset, because the offset that matters is the one in
 * force on *that* day and DST moves it. Undefined on a runtime without `Intl`
 * — the server then compares stored dates, which is what it did before.
 */
function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
}

export function eventRadarQuery(goal?: GoalType, days = 45, day?: string | null) {
  return queryOptions({
    // The goal is part of the key: the same pool ranks completely differently
    // per track, so a cached "cust" answer must not be served for "fund".
    //
    // So is the day, and for a sharper reason: the server narrows the *window*
    // before it keeps the top 24, so a one-day answer is not a subset of the
    // whole-window one. Sharing a key would serve twenty-four rooms from six
    // weeks as if they were Tuesday's.
    queryKey: ["api", "radar", "events", { goal, days, day: day ?? null }],
    queryFn: async () => {
      const res = await api.radar.events.$get({
        query: {
          days: String(days),
          ...(goal ? { goal } : {}),
          ...(day ? { day, tz: localTimeZone() } : {}),
        },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

/**
 * `enabled` matters here: the ranking is per track, so firing before the
 * active goal is known costs a full round trip whose answer is thrown away
 * the moment settings resolve — two requests, one useful.
 */
export function useEventRadar(
  goal?: GoalType,
  days?: number,
  enabled = true,
  day?: string | null,
) {
  return useQuery({ ...eventRadarQuery(goal, days, day), enabled });
}

/**
 * The rooms she picked — her week, not the city's.
 *
 * Separate from `eventRadarQuery` on purpose: that one answers "what is on",
 * and Home has to answer "what am I going to". A week filled with everything
 * available is research; she opens that screen to see her own.
 */
export function pickedEventsQuery(days = 30, from?: string) {
  return queryOptions({
    // `from` is part of the key, so paging a calendar back a month is a
    // different cached answer rather than a silent overwrite of this month's.
    queryKey: ["api", "events", "picked", { days, from }],
    queryFn: async () => {
      const res = await api.events.picked.$get({
        query: { days: String(days), ...(from ? { from } : {}) },
      });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).picked;
    },
  });
}

/** `from` lets the calendar ask for the exact span its grid shows — including
 *  the neighbouring days a month view displays, so a room on 31 July is not
 *  missing from the August grid that shows it. */
export function usePickedEvents(days?: number, from?: string) {
  return useQuery(pickedEventsQuery(days, from));
}

/** Going / not going. Invalidates the radar too, so a picked room is marked
 *  there without a refetch loop of its own. */
export function usePickEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, going }: { id: string; going: boolean }) => {
      const res = going
        ? await api.events[":id"].pick.$post({ param: { id } })
        : await api.events[":id"].pick.$delete({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "events", "picked"] });
      void qc.invalidateQueries({ queryKey: ["api", "radar", "events"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// The calendars behind the base
// ─────────────────────────────────────────────────────────────

/** Which calendars are being watched, and whether each still answers. */
export function useEventSources() {
  return useQuery({
    queryKey: ["api", "event-sources"],
    queryFn: async () => {
      const res = await api["event-sources"].$get();
      if (!res.ok) throw await apiError(res);
      return (await res.json()).sources;
    },
  });
}

/**
 * Ask the web for calendars worth watching.
 *
 * A mutation rather than a query because it costs real Exa calls and should
 * happen when she asks, not when a component mounts.
 */
export function useSuggestSources() {
  return useMutation({
    mutationFn: async () => {
      const res = await api["event-sources"].suggest.$get();
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export interface EventSourceBody {
  id: string;
  name: string;
  kind: "luma-ics" | "meetup-ics" | "ics";
  url: string;
  region: string;
  vertical?: string | null;
}

/** Approve a calendar. After this the cron reads it every few hours and its
 *  events reach the radar — the moment a one-off find becomes supply. */
export function useAddEventSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EventSourceBody) => {
      const res = await api["event-sources"].$post({ json: body });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "event-sources"] });
      void qc.invalidateQueries({ queryKey: ["api", "radar", "events"] });
    },
  });
}

/** Stop watching. Deactivated rather than deleted, so a calendar already
 *  judged is not offered back as a suggestion. */
export function useRemoveEventSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api["event-sources"][":id"].$delete({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "event-sources"] });
      void qc.invalidateQueries({ queryKey: ["api", "radar", "events"] });
    },
  });
}

/** Judged and refused. The url is remembered so the radar never offers it
 *  again — a radar that repeats itself is one she stops opening. */
export function useDismissCandidates() {
  return useMutation({
    mutationFn: async (urls: string[]) => {
      const res = await api.radar.seen.$post({ json: { urls } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export function useRemoveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.rooms[":id"].$delete({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "rooms"] });
      void qc.invalidateQueries({ queryKey: ["api", "maps"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Follow up — the loop
// ─────────────────────────────────────────────────────────────

export const contactsQuery = queryOptions({
  queryKey: ["api", "contacts"],
  queryFn: async () => {
    const res = await api.contacts.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).contacts;
  },
});

export function useContacts() {
  return useQuery(contactsQuery);
}

export function movesQuery(contactId?: string) {
  return queryOptions({
    queryKey: contactId ? ["api", "moves", contactId] : ["api", "moves"],
    queryFn: async () => {
      const res = await api.moves.$get({
        query: contactId ? { contactId } : {},
      });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).moves;
    },
  });
}

export function useMoves(contactId?: string) {
  return useQuery(movesQuery(contactId));
}

export const outcomesQuery = queryOptions({
  queryKey: ["api", "outcomes"],
  queryFn: async () => {
    const res = await api.outcomes.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).outcomes;
  },
});

export function useOutcomes() {
  return useQuery(outcomesQuery);
}

/** What the brain learned — the compounding arrow, made visible. */
export const loopSummaryQuery = queryOptions({
  queryKey: ["api", "loop", "summary"],
  queryFn: async () => {
    const res = await api.loop.summary.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useLoopSummary() {
  return useQuery(loopSummaryQuery);
}

// ─────────────────────────────────────────────────────────────
// The loop's two writes, applied optimistically
//
// Recording a move and logging an outcome must feel instant: rule #6 says one
// tap, and a tap that visibly waits on a round trip is how the habit dies. The
// next state is computed by the *same pure reducers the server's behaviour
// mirrors* (`recordMove`, `logOutcome` in @omina/core) rather than by ad-hoc
// cache patching — so the optimistic frame and the refetched one agree, and
// the transition rules live in one tested place instead of two.
// ─────────────────────────────────────────────────────────────

/** The three cached slices, lifted into the shape the reducers reason about.
 *  Null when any slice has never been fetched — there is nothing to predict
 *  from, so those mutations just wait for the server. */
function readLoop(qc: QueryClient): LoopData | null {
  const contacts = qc.getQueryData(contactsQuery.queryKey);
  const moves = qc.getQueryData(movesQuery().queryKey);
  const outcomes = qc.getQueryData(outcomesQuery.queryKey);
  if (!contacts || !moves || !outcomes) return null;
  return { contacts, moves, outcomes };
}

function writeLoop(qc: QueryClient, loop: LoopData): void {
  qc.setQueryData(contactsQuery.queryKey, loop.contacts);
  qc.setQueryData(movesQuery().queryKey, loop.moves);
  qc.setQueryData(outcomesQuery.queryKey, loop.outcomes);
}

/** Everything the loop touches: the three lists and the summary derived from
 *  them. They go stale together because they change together. */
function invalidateLoop(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ["api", "contacts"] });
  void qc.invalidateQueries({ queryKey: ["api", "moves"] });
  void qc.invalidateQueries({ queryKey: ["api", "outcomes"] });
  void qc.invalidateQueries({ queryKey: ["api", "loop"] });
}

/** See ProfileBody: inputs are mirrored by hand, responses are inferred. */
export interface ContactBody {
  name: string;
  context?: string;
  company?: string | null;
  role?: string | null;
  email?: string | null;
  linkedinUrl?: string | null;
  source?: "manual" | "import" | "calendar" | "gmail" | "linkedin";
}

export interface ContactPatchBody {
  name?: string;
  context?: string;
  company?: string | null;
  role?: string | null;
  email?: string | null;
  linkedinUrl?: string | null;
  connectedOn?: string | null;
  metAt?: MetAt | null;
  birthday?: string | null;
  /** Sent as she typed them; the server folds them to their stored form. */
  labels?: string[] | null;
}

/**
 * Edit a contact. Confirming where someone was met is this, not its own
 * endpoint — `metAt` and `context` are written together in one tap, because
 * `metAt` is the record and `context` is what the draft prompt actually reads.
 */
export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string } & ContactPatchBody) => {
      const { id, ...patch } = vars;
      const res = await api.contacts[":id"].$patch(
        withBody({ param: { id } }, patch satisfies ContactPatchBody),
      );
      if (!res.ok) throw await apiError(res);
      return (await res.json()).contact;
    },
    onSuccess: () => invalidateLoop(qc),
  });
}

/** Adding someone is not optimistic on purpose: the server mints the id, and
 *  the draft that follows is addressed to it. */
export function useAddContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ContactBody) => {
      const res = await api.contacts.$post({ json: body });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).contact;
    },
    onSuccess: () => invalidateLoop(qc),
  });
}

export interface RecordMoveVars {
  contactId: string;
  draft: string;
  subject?: string | null;
  /** LinkedIn is recorded, never sent — rule #5. */
  channel?: "email" | "linkedin" | "other";
}

/** A follow-up she actually sent. Also advances the contact out of "new",
 *  client-side and server-side, from the same rule. */
export function useRecordMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RecordMoveVars) => {
      const res = await api.moves.$post({
        json: {
          contactId: vars.contactId,
          draft: vars.draft,
          subject: vars.subject ?? null,
          channel: vars.channel ?? "email",
        },
      });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).move;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["api"] });
      const previous = readLoop(qc);
      if (previous) writeLoop(qc, recordMove(previous, vars.contactId, vars.draft).data);
      return previous;
    },
    onError: (_error, _vars, previous) => {
      if (previous) writeLoop(qc, previous);
    },
    onSettled: () => invalidateLoop(qc),
  });
}

export interface LogOutcomeVars {
  contactId: string;
  result: OutcomeResult;
  moveId?: string;
}

/** One tap. The whole moat is that this stays one tap. */
export function useLogOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: LogOutcomeVars) => {
      const res = await api.outcomes.$post({
        json: {
          contactId: vars.contactId,
          result: vars.result,
          ...(vars.moveId ? { moveId: vars.moveId } : {}),
        },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["api"] });
      const previous = readLoop(qc);
      if (previous) writeLoop(qc, logOutcome(previous, vars.contactId, vars.result));
      return previous;
    },
    onError: (_error, _vars, previous) => {
      if (previous) writeLoop(qc, previous);
    },
    onSettled: () => invalidateLoop(qc),
  });
}

// ─────────────────────────────────────────────────────────────
// Calendar feeds
// ─────────────────────────────────────────────────────────────

/** Which feeds exist — never their URLs. Those are bearer credentials: the
 *  API stores them encrypted and will not read one back out. */
export const feedsQuery = queryOptions({
  queryKey: ["api", "feeds"],
  queryFn: async () => {
    const res = await api.feeds.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).feeds;
  },
});

export function useFeeds() {
  return useQuery(feedsQuery);
}

/**
 * Store or remove a calendar feed.
 *
 * `url: null` removes it. The URL is write-only by design — the API encrypts
 * it and will not read one back out — so the UI can say a feed is connected
 * and offer to replace it, never to show it.
 */
export function useSaveFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { kind: "gcal" | "luma"; url: string | null }) => {
      const res = await api.feeds.$put({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "feeds"] });
      void qc.invalidateQueries({ queryKey: ["api", "events"] });
    },
  });
}

export function eventsQuery(pastDays = 30, futureDays = 7, limit = 24) {
  return queryOptions({
    queryKey: ["api", "events", { pastDays, futureDays, limit }],
    queryFn: async () => {
      const res = await api.events.$get({
        query: {
          pastDays: String(pastDays),
          futureDays: String(futureDays),
          limit: String(limit),
        },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export function useEvents(pastDays?: number, futureDays?: number) {
  return useQuery(eventsQuery(pastDays, futureDays));
}

// ─────────────────────────────────────────────────────────────
// The legacy migration
// ─────────────────────────────────────────────────────────────

/**
 * Import the prototype's export.
 *
 * `confirm: false` is a dry run: the server plans every row and answers with
 * counts, writing nothing. `confirm: true` **wipes and replaces** this
 * workspace's loop, maps and rooms — which is what makes a failed import safe
 * to retry, and why the UI never sends it without a second, explicit click.
 */
export function useImportLegacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { export: LegacyExport; confirm: boolean }) => {
      const res = await api.import.$post({
        json: { confirm: vars.confirm, export: vars.export },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: (_data, vars) => {
      // A dry run changed nothing; invalidating after one would just refetch
      // the whole app for no reason.
      if (vars.confirm) void qc.invalidateQueries({ queryKey: ["api"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// LinkedIn — connections, by the one legitimate route
// ─────────────────────────────────────────────────────────────

/** Whether consent exists and how much of it is left. Never the token: the
 *  API stores it encrypted and will not read one back out. */
export const linkedinQuery = queryOptions({
  queryKey: ["api", "linkedin"],
  queryFn: async () => {
    const res = await api.linkedin.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useLinkedIn() {
  return useQuery(linkedinQuery);
}

/**
 * Read a slice of the connections export.
 *
 * `confirm: false` plans and writes nothing. `start` is a cursor: Workers
 * allow 50 subrequests per invocation, so a large network arrives across
 * several calls and the caller loops until `nextStart` is null.
 */
export function useSyncLinkedIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { confirm: boolean; start?: number }) => {
      const res = await api.linkedin.sync.$post({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: (_data, vars) => {
      if (vars.confirm) {
        invalidateLoop(qc);
        void qc.invalidateQueries({ queryKey: ["api", "linkedin"] });
      }
    },
  });
}

/**
 * What Google granted, and how the last sync went.
 *
 * `needsReconsent` is the field worth watching: it is true for a founder who
 * signed in before Calendar and Gmail were part of the consent screen. Her
 * session works, her tokens are valid, and every read Google gets asked for
 * comes back empty — a state that is indistinguishable from "a quiet week"
 * unless the card says so.
 */
export const googleStatusQuery = queryOptions({
  queryKey: ["api", "google", "status"],
  queryFn: async () => {
    const res = await api.google.status.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useGoogleStatus() {
  return useQuery(googleStatusQuery);
}

/**
 * Sync now.
 *
 * Bounded per call, exactly like the cron tick it shares code with, and it
 * answers `more` so the caller loops. A first pass over ninety days of mail is
 * several rounds — she should watch a number climb rather than a spinner that
 * might be dead.
 */
export function useSyncGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service?: "calendar" | "gmail" } = {}) => {
      const res = await api.google.sync.$post({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "google"] });
      void qc.invalidateQueries({ queryKey: ["api", "gcal"] });
      void qc.invalidateQueries({ queryKey: ["api", "gmail"] });
      // A sync is the one thing that can turn an empty rail into a full one.
      void qc.invalidateQueries({ queryKey: ["api", "connections", "suggestions"] });
    },
  });
}

/**
 * The Microsoft twin of `googleStatusQuery`.
 *
 * Separate rather than merged because the settings page shows two cards and a
 * founder usually has one provider: a combined call would make each card wait
 * on the other's answer.
 */
export const microsoftStatusQuery = queryOptions({
  queryKey: ["api", "microsoft", "status"],
  queryFn: async () => {
    const res = await api.microsoft.status.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useMicrosoftStatus() {
  return useQuery(microsoftStatusQuery);
}

export function useSyncMicrosoft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service?: "calendar" | "mail" } = {}) => {
      const res = await api.microsoft.sync.$post({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "microsoft"] });
      void qc.invalidateQueries({ queryKey: ["api", "gcal"] });
      void qc.invalidateQueries({ queryKey: ["api", "gmail"] });
      // Outlook fills the same tables Google does, so the rail changes too.
      void qc.invalidateQueries({ queryKey: ["api", "connections", "suggestions"] });
      void qc.invalidateQueries({ queryKey: ["api", "curation"] });
    },
  });
}

/**
 * Who the last ninety days say she already knows.
 *
 * Recomputed by the server on every read — there is no stored list to go
 * stale, which is why this can be invalidated freely and why the answer
 * changes the moment she adds or dismisses someone.
 */
export const connectionSuggestionsQuery = queryOptions({
  queryKey: ["api", "connections", "suggestions"],
  queryFn: async () => {
    const res = await api.connections.suggestions.$get({ query: {} });
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useConnectionSuggestions() {
  return useQuery(connectionSuggestionsQuery);
}

/** Yes — a suggestion becomes a contact, and the circle gains a person. */
export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      name?: string;
      source?: "calendar" | "gmail";
      metAt?: {
        eventTitle?: string;
        eventDate?: string;
        source: "confirmed";
        recordedAt: string;
      };
    }) => {
      const res = await api.connections.suggestions.accept.$post({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "connections", "suggestions"] });
      invalidateLoop(qc);
    },
  });
}

/** No — the only thing a refusal writes, and what stops the same face coming
 *  back every week. */
export function useDismissSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const res = await api.connections.suggestions.dismiss.$post({ json: vars });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "connections", "suggestions"] });
    },
  });
}

export function useDisconnectLinkedIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { purge: boolean }) => {
      const res = await api.linkedin.$delete({ query: vars.purge ? { purge: "1" } : {} });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "linkedin"] });
      invalidateLoop(qc);
    },
  });
}

/** The same merge, from the archive CSV — the only route outside the EEA,
 *  and the fallback when consent runs out. */
export function useImportConnections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { connections: Record<string, string>[]; confirm: boolean }) => {
      const res = await api.connections.import.$post({
        json: { confirm: vars.confirm, connections: vars.connections },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: (_data, vars) => {
      if (vars.confirm) invalidateLoop(qc);
    },
  });
}

// ─────────────────────────────────────────────────────────────
// The debrief
// ─────────────────────────────────────────────────────────────

export const eventFeedbackQuery = queryOptions({
  queryKey: ["api", "event-feedback"],
  queryFn: async () => {
    const res = await api["event-feedback"].$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).feedback;
  },
});

export function useEventFeedback() {
  return useQuery(eventFeedbackQuery);
}

export interface EventFeedbackBody {
  eventTitle: string;
  eventDate: string;
  worthIt: boolean;
  note?: string | null;
}

/** One tap after an event. Re-tapping corrects it — the server upserts on
 *  (org, title, date) — so a mis-tap is not a permanent second opinion. */
export function useSaveEventFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EventFeedbackBody) => {
      const res = await api["event-feedback"].$post({ json: body });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "event-feedback"] });
      // A debrief can move `converted` on an approved room, which the map
      // composes from.
      void qc.invalidateQueries({ queryKey: ["api", "rooms"] });
      void qc.invalidateQueries({ queryKey: ["api", "maps"] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Research
// ─────────────────────────────────────────────────────────────

/** Research one contact. Never called in bulk — rule #7. */
export function useEnrichContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; force?: boolean }) => {
      const res = await api.contacts[":id"].enrich.$post(
        withBody({ param: { id: vars.id } }, { force: vars.force ?? false }),
      );
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => invalidateLoop(qc),
  });
}

/** "This isn't them." Clears a wrong-person profile in one tap. */
export function useClearEnrichment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const res = await api.contacts[":id"].enrich.$delete({ param: { id: vars.id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => invalidateLoop(qc),
  });
}

// ─────────────────────────────────────────────────────────────
// Invitations that arrived by email
// ─────────────────────────────────────────────────────────────

/**
 * Who has asked to connect and is waiting on her.
 *
 * A queue, not a feed into the circle: an invitation email says somebody
 * clicked connect, not that they belong there. Recruiters arrive down the same
 * pipe as the person she actually met.
 */
export const invitesQuery = queryOptions({
  queryKey: ["api", "invites"],
  queryFn: async () => {
    const res = await api.invites.$get();
    if (!res.ok) throw await apiError(res);
    return (await res.json()).invites;
  },
});

export function useInvites() {
  return useQuery(invitesQuery);
}

export interface AddInviteBody {
  /** Required when the parser could not read a name off the subject. */
  name?: string;
  company?: string | null;
  role?: string | null;
  context?: string;
  metAt?: MetAt | null;
  /** Sent with the same tap that confirms the room, so the `met-<slug>` that
   *  makes her findable by event never lands in a second request that can
   *  fail on its own. */
  labels?: string[];
  /** Their profile, read off the notification mail that announced them. */
  linkedinUrl?: string;
}

/** One tap admits them. Invalidates the loop, because a new contact changes the
 *  circle, the follow-up queue and the counts on Today. */
export function useAddInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: AddInviteBody & { id: string }) => {
      const res = await api.invites[":id"].add.$post(
        // Same param-only inference problem `withBody` exists for: hono types
        // this route as `{ param }`, so the body has to be handed over widened.
        withBody({ param: { id } }, body satisfies AddInviteBody),
      );
      if (!res.ok) throw await apiError(res);
      return (await res.json()).contact;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "invites"] });
      invalidateLoop(qc);
    },
  });
}

/** One tap refuses them. Recorded rather than deleted, so a LinkedIn resend
 *  does not offer the same person forever. */
export function useDismissInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.invites[":id"].$delete({ param: { id } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api", "invites"] }),
  });
}

/**
 * Whether LinkedIn mail reaches the connected mailbox at all.
 *
 * An empty queue has two unrelated causes that look identical on screen:
 * nothing has arrived yet, or nothing ever will — notifications switched off,
 * or a LinkedIn account registered to a different address than the one she
 * connected. Only the second is worth interrupting her for, and she cannot fix
 * it if the screen just says "no new connections".
 */
export const invitesPreflightQuery = queryOptions({
  queryKey: ["api", "invites", "preflight"],
  queryFn: async () => {
    const res = await api.invites.preflight.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
  // The probe costs a Gmail call; the answer changes about never.
  staleTime: 10 * 60 * 1000,
});

/**
 * Faces for the queue, from the notification mail that announced each person.
 *
 * Its own query because it is the one part of that screen that costs a network
 * call — the cards render fully without it, and a face that never arrives is a
 * monogram rather than an error. `staleTime` keeps a scroll or a tab switch
 * from re-reading her mailbox; it is short because the queue itself changes as
 * she works through it, not because the URLs go stale.
 */
export const inviteAvatarsQuery = queryOptions({
  queryKey: ["api", "invites", "avatars"],
  queryFn: async () => {
    const res = await api.invites.avatars.$get();
    if (!res.ok) throw await apiError(res);
    // `avatars` is keyed by invite id (the queue), `contacts` by contact id
    // (people already admitted). One call serves both so a face does not
    // disappear at the moment she taps Add.
    return res.json();
  },
  staleTime: 5 * 60 * 1000,
  // A missing photo is not worth a retry storm behind a slow mailbox.
  retry: false,
});

export function useInviteAvatars(enabled: boolean) {
  return useQuery({
    ...inviteAvatarsQuery,
    enabled,
    select: (d: { avatars: Record<string, string> }) => d.avatars,
  });
}

/** LinkedIn profiles read off the notification mail, keyed by invite id and
 *  by contact id. Shares the request the faces already make. */
export function useProfiles(enabled: boolean) {
  return useQuery({
    ...inviteAvatarsQuery,
    enabled,
    select: (d: { profiles: Record<string, string> }) => d.profiles,
  });
}

/** Headlines read off the notification mail, keyed by invite id and contact
 *  id. LinkedIn states a role in the subject only on an invitation, so for an
 *  acceptance this is the only place one exists without a paid lookup. */
export function useHeadlines(enabled: boolean) {
  return useQuery({
    ...inviteAvatarsQuery,
    enabled,
    select: (d: { roles: Record<string, string> }) => d.roles,
  });
}

/** Faces for people already in the circle, keyed by contact id. */
export function useContactAvatars(enabled: boolean) {
  return useQuery({
    ...inviteAvatarsQuery,
    enabled,
    select: (d: { contacts: Record<string, string> }) => d.contacts,
  });
}

export function useInvitesPreflight() {
  return useQuery(invitesPreflightQuery);
}

/**
 * Her real calendar, out of D1.
 *
 * Not `eventsQuery` — that one fetches the ICS feeds she pasted in, and a
 * founder who signed in with Google has no feeds at all. Both are merged
 * wherever rooms are proposed, because either can be the only one she has.
 */
export function gcalEventsQuery(fromIso: string, toIso: string, limit = 200) {
  return queryOptions({
    queryKey: ["api", "gcal", "events", fromIso, toIso, limit],
    queryFn: async () => {
      const res = await api.gcal.events.$get({
        query: { from: fromIso, to: toIso, limit: String(limit) },
      });
      if (!res.ok) throw await apiError(res);
      return (await res.json()).events;
    },
  });
}

/**
 * The inbox connection and its health.
 *
 * Read-only, so it is a query. `lastReceivedAt` is the field that matters: this
 * pipeline fails silently — a rotated address, a deleted mail filter, a provider
 * that stopped forwarding — and all three look identical from her side, which is
 * "nobody has appeared in a while".
 */
export const inboundAddressQuery = queryOptions({
  queryKey: ["api", "inbound-address"],
  queryFn: async () => {
    const res = await api["inbound-address"].$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useInboundAddress() {
  return useQuery(inboundAddressQuery);
}

/**
 * Mint or rotate the address.
 *
 * A mutation rather than a query because asking for it *creates* it — a
 * workspace that never sets up forwarding never gets a token, and a token that
 * exists is a token that can leak. Rotating is the same call: the only remedy
 * for a leaked bearer address is a new one.
 */
export function useMintInboundAddress() {
  const qc = useQueryClient();
  return useMutation({
    // Typed explicitly: an optional parameter makes useMutation infer `void`
    // for its variables, and then `mutate(true)` stops compiling.
    mutationFn: async (rotate: boolean) => {
      const res = await api["inbound-address"].$post({
        query: rotate ? { rotate: "1" } : {},
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api", "inbound-address"] }),
  });
}

// ─────────────────────────────────────────────────────────────
// Curation — the ninety days she already lived
// ─────────────────────────────────────────────────────────────

/**
 * The review queue, plus the answer sets that go with it.
 *
 * The options ship inside this response rather than being imported from
 * `@omina/core` on the client. That is on purpose: the same route validates
 * writes against them, so a chip on screen is by construction a chip the
 * server accepts. Importing the arrays separately would compile perfectly and
 * then 400 the first time the two drifted.
 *
 * `staleTime: 0` because answering removes an event from this list. Anything
 * else shows her a card she has already judged.
 */
/**
 * How many rooms one sitting asks about.
 *
 * Ninety days of a real calendar is over a hundred past events, and a queue
 * that long is a chore rather than an onboarding step — the counter reading
 * "1 of 120" tells her she has bought into something enormous before she has
 * answered anything. Four is under a minute, it is finishable in onboarding
 * without stalling her there, and four verdicts are already enough for the
 * radar to stop guessing blind.
 *
 * The rest are not lost. They stay `pending` and the queue refills next time
 * she opens it, so this is a session length rather than a cap on the work.
 */
export const CURATION_BATCH = 4;

export const curationQueueQuery = queryOptions({
  queryKey: ["api", "curation", "events"],
  queryFn: async () => {
    const res = await api.curation.events.$get({
      query: { limit: String(CURATION_BATCH) },
    });
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
  staleTime: 0,
});

export function useCurationQueue() {
  return useQuery(curationQueueQuery);
}

/**
 * Her verdict on one event.
 *
 * Invalidates the loop as well as the queue: a public room she liked becomes a
 * debrief server-side, which is what `approved_rooms.converted` compounds
 * from, so the map and the summary are genuinely stale afterwards.
 */
export function useCurateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      // Includes `personal` — the "this was never a room" answer, which
      // carries neither a sentiment nor a relationship.
      eventKind: "public" | "people_meeting" | "personal";
      overallSentiment?: string | null;
      relationshipType?: string | null;
      selectedOptions?: string[];
    }) => {
      const { id, ...body } = vars;
      // `withBody` because this route parses its body by hand rather than
      // through a validator, so the RPC types carry no `json` key to satisfy.
      const res = await api.curation.events[":id"].feedback.$post(
        withBody({ param: { id } }, body),
      );
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["api", "curation"] });
      invalidateLoop(qc);
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Interests — the substitute for a calendar
// ─────────────────────────────────────────────────────────────

/** Questions and saved answers together, so the chips on screen are always
 *  the chips the server will accept. */
export const interestsQuery = queryOptions({
  queryKey: ["api", "interests"],
  queryFn: async () => {
    const res = await api.interests.$get();
    if (!res.ok) throw await apiError(res);
    return res.json();
  },
});

export function useInterests() {
  return useQuery(interestsQuery);
}

export function useSaveInterests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (answers: Record<string, string[]>) => {
      const res = await api.interests.$put({ json: { answers } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: async () => {
      void qc.invalidateQueries({ queryKey: ["api", "interests"] });
      // The profile carries them, and the radar reads the profile.
      void qc.invalidateQueries({ queryKey: ["api", "profile"] });
      void qc.invalidateQueries({ queryKey: ["api", "radar"] });
      /**
       * And `me`, awaited, because the router's onboarding gate reads
       * `interestsDone` off it.
       *
       * `ensureMe` serves whatever is cached, and what is cached the instant
       * before this mutation is "not done". Invalidating without waiting marks
       * it stale and returns immediately — so the navigation this mutation
       * triggers runs the gate against the old answer and throws her straight
       * back to the screen she just finished.
       *
       * `refetchType: "all"` is the part that does the work, and the default
       * is the trap: invalidation only refetches *active* queries, and nothing
       * on this screen observes `me`. So the plain call marked it stale, awaited
       * nothing, and left the gate reading the same stale answer — the loop was
       * still there, just with an await in front of it.
       */
      await qc.invalidateQueries({ queryKey: ["api", "me"], refetchType: "all" });
    },
  });
}

/** Used by the router's guard, which runs outside React. */
export function ensureMe(queryClient: QueryClient) {
  return queryClient.ensureQueryData(meQuery);
}

// ─────────────────────────────────────────────────────────────
// The room brief — who is in the room she is walking into
// ─────────────────────────────────────────────────────────────

/**
 * Upcoming rooms off her calendar, each with what its public page will say.
 *
 * Separate from `pickedEventsQuery` above because it answers a third question.
 * That one is "what am I going to"; this is "who will be there, and is it
 * worth the evening". The server does the reading — one public page per room,
 * cached globally — so this hook is a plain read with no options of its own.
 */
export function roomBriefQuery(days = 30) {
  return queryOptions({
    queryKey: ["api", "room-brief", { days }],
    queryFn: async () => {
      const res = await api["room-brief"].$get({ query: { days: String(days) } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export function useRoomBrief(days?: number) {
  return useQuery(roomBriefQuery(days));
}

/**
 * What a model made of one room — fetched when she opens it.
 *
 * A POST behind a query rather than a mutation, because the answer is cached
 * per room and re-opening should not re-ask. `enabled` is what makes it lazy:
 * a room she never expands is never read, so a screen of five rooms costs
 * nothing until she is curious about one of them.
 */
export function roomReadQuery(url: string, enabled: boolean) {
  return queryOptions({
    queryKey: ["api", "room-brief", "read", url],
    enabled,
    // The read is cached server-side for a week; this stops a re-expand in the
    // same session from even asking.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await api["room-brief"].read.$post({ json: { url } });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}

export function useRoomRead(url: string, enabled: boolean) {
  return useQuery(roomReadQuery(url, enabled));
}

/**
 * Research one person from a room, on her tap.
 *
 * Invalidates nothing, because it writes nothing: a person an event page named
 * is a suggestion until she adds them. The answer lives in this mutation's own
 * state for as long as the card is open, which is the right lifetime for a
 * fact about somebody who is not in her circle yet.
 */
export function useResearchRoomPerson() {
  return useMutation({
    mutationFn: async (person: { name: string; org?: string; role?: string }) => {
      const res = await api["room-brief"].research.$post({ json: person });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
  });
}
